import firebasePromise from "./firebase";
import apiPromise, {fetchJson} from "./api";

// Resolves with the current Firebase user, or null if no user is signed in
// once Firebase has finished hydrating its auth state from persistence.
// The first onAuthStateChanged emission can fire with null even when a user
// IS signed in (before persistence is loaded), so we prefer auth.currentUser
// when the observer fires with null but the state is already populated.
function getCurrentUser(auth) {
    return new Promise((resolve, reject) => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            unsubscribe();
            resolve(user || auth.currentUser || null);
        }, reject);
    });
}

// Decodes the payload of a base64url-encoded JWT (no signature verification —
// we only use claims for UI gating; the backend revalidates every request).
const decodeJwtPayload = (token) => {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    try {
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "===".slice((b64.length + 3) % 4);
        return JSON.parse(atob(padded));
    } catch (e) {
        return null;
    }
};

const fetchAccessToken = async () => {
    const firebase = await firebasePromise;
    const api = await apiPromise;
    const currentUser = await getCurrentUser(firebase.auth());
    if (!currentUser) {
        // No signed-in Firebase user — caller likely shouldn't have invoked us.
        // Throw clearly instead of crashing on `.getIdToken` of null.
        throw new Error("fetchAccessToken: no Firebase user signed in");
    }
    const formData = new FormData();
    const idToken = await currentUser.getIdToken(false);

    formData.append("grant_type", "urn:ietf:params:oauth:grant-type:token-exchange");
    formData.append("subject_token", idToken);
    formData.append("subject_token_type", "urn:ietf:params:oauth:token-type:id_token");

    const tokenResponse =  await fetchJson(api._links.token.href, {
        method: 'POST',
        body: formData,
    });

    return tokenResponse.json.access_token;
};

export const updateAccessToken = async () => {
    const token = await fetchAccessToken();
    localStorage.setItem("token", token);

    // Surface the authorities claim so usePermissions() in react-admin sees them.
    const payload = decodeJwtPayload(token);
    const authorities = (payload && Array.isArray(payload.authorities)) ? payload.authorities : [];
    localStorage.setItem("permissions", JSON.stringify(authorities));
};

export const getAccessToken = async () => {
    if (localStorage.getItem("token") === null) {
        await updateAccessToken();
    }

    return localStorage.getItem("token");
};

